import React, { useMemo, type JSX } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import {
  createDefaultAdminListTimeRange,
  parseRecognitionAuditQueryParams,
} from "../../contracts/admin-route-params";
import type {
  AdminRecognitionAuditQuery,
} from "../../contracts/admin-read-models";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import {
  clearExplicitAdminTimeRangeSearchParams,
  resolveExplicitAdminTimeRangeUpperBound,
  resolveAdminLiveTimeRangeRefreshSeed,
  useAdminAutoRefreshTick,
} from "../../shared/query/admin-auto-refresh";
import { useAdminManualRefreshRegistration } from "../../shared/query/admin-manual-refresh-context";
import { AdminAggregateSummaryStrip } from "../../shared/ui/admin-aggregate-summary-strip";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminLiveRangeBanner } from "../../shared/ui/admin-live-range-banner";
import { AdminSourceStatusGrid } from "../../shared/ui/admin-source-status-grid";
import { AdminStatusBadge } from "../../shared/ui/admin-status-badge";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import { RecognitionAuditFilterBar } from "./recognition-audit-filter-bar";
import {
  createDefaultRecognitionAuditQuery,
  useRecognitionAuditScreenModel,
  type RecognitionAuditScreenModelState,
} from "./use-recognition-audit-screen-model";

export type RecognitionAuditScreenProps = {
  dependencies: AdminDashboardDependencies;
};

function cardStyle() {
  return {
    padding: "18px",
    borderRadius: "18px",
    background: "rgba(255, 255, 255, 0.82)",
    border: "1px solid rgba(24, 33, 43, 0.08)",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.06)",
  } as const;
}

function toSearchParams(query: AdminRecognitionAuditQuery, locale: "ko" | "en"): URLSearchParams {
  const params = new URLSearchParams();
  params.set("from", query.timeRange.from);
  params.set("to", query.timeRange.to);
  params.set("limit", String(query.limit));
  params.set("sort", query.sort);

  for (const value of query.documentKinds) {
    params.append("documentKind", value);
  }
  for (const value of query.statuses) {
    params.append("status", value);
  }
  for (const value of query.outcomes) {
    params.append("outcome", value);
  }

  for (const [key, value] of Object.entries({
    failedStage: query.failedStage,
    errorCode: query.errorCode,
    aiProvider: query.aiProvider,
    aiModel: query.aiModel,
    accountId: query.accountId,
    petId: query.petId,
    cursor: query.cursor,
    sourceFreshness: query.sourceFreshness,
  })) {
    if (typeof value === "string" && value.length > 0) {
      params.set(key, value);
    }
  }

  return withAdminDashboardLocaleSearchParams(params, locale);
}

export function buildRecognitionAuditDetailPath(
  recognitionRunId: string,
  searchParams: URLSearchParams,
): string {
  const path = `/admin/recognition-audits/${encodeURIComponent(recognitionRunId)}`;
  const queryString = searchParams.toString();
  return queryString.length > 0 ? `${path}?${queryString}` : path;
}

export function createRecognitionAuditRowClickHandler(
  navigate: (path: string) => void,
  searchParams: URLSearchParams,
): (recognitionRunId: string) => void {
  return (recognitionRunId) => {
    navigate(buildRecognitionAuditDetailPath(recognitionRunId, searchParams));
  };
}

export function resolveRecognitionAuditListSearchParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  return new URLSearchParams(searchParams);
}

export function resolveRecognitionAuditRefreshSeed(
  searchParams: URLSearchParams,
  refreshTick: number,
): number {
  return searchParams.has("cursor")
    ? 0
    : resolveAdminLiveTimeRangeRefreshSeed(searchParams, refreshTick);
}

function buildSourceStatusItems(
  locale: "ko" | "en",
  state: RecognitionAuditScreenModelState,
): Array<{ source: string; freshness: "fresh" | "stale" | "partial" | "unavailable"; sourceKindLabel: string }> {
  const sourceStatus = state.page?.sourceStatus.kind ?? "live";
  const freshness = sourceStatus === "partial" ? "partial" : "fresh";

  return [
    {
      source: "projection freshness",
      freshness,
      sourceKindLabel: pickAdminDashboardLocaleText(locale, { en: "query", ko: "조회" }),
    },
    {
      source: "latest source activity",
      freshness,
      sourceKindLabel: state.page?.generatedAtLabel ?? "-",
    },
    {
      source: "latest raw access",
      freshness,
      sourceKindLabel: state.detailData?.rawAccessHistory[0]?.accessedAt
        ? formatAdminDate(state.detailData.rawAccessHistory[0].accessedAt)
        : "-",
    },
    {
      source: "cost pricing policy version",
      freshness,
      sourceKindLabel: state.detailData?.aiUsage[0]?.pricingVersion ?? "-",
    },
  ];
}

function renderSummaryCards(
  locale: "ko" | "en",
  state: RecognitionAuditScreenModelState,
): JSX.Element {
  const summary = state.page?.summary;
  const resultItems = [
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Recent runs", ko: "최근 인식 시도" }),
      value: summary?.totalRuns.label ?? "0",
    },
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Failed / cancelled", ko: "실패/취소" }),
      value: summary?.failedOrCancelledRuns.label ?? "0",
    },
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Final saved", ko: "최종 저장" }),
      value: summary?.finalSavedRuns.label ?? "0",
    },
  ];
  const averageItems = [
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Files", ko: "파일" }),
      metric: summary?.averageSourceFileCount,
    },
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Images", ko: "이미지" }),
      metric: summary?.averageSourceImageCount,
    },
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Source size", ko: "원본 용량" }),
      metric: summary?.averageSourceBytes,
    },
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Raw JSON size", ko: "원문 JSON 용량" }),
      metric: summary?.averageRawResponseBytes,
    },
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Tokens", ko: "토큰" }),
      metric: summary?.averageTotalTokens,
    },
    {
      label: pickAdminDashboardLocaleText(locale, { en: "Estimated cost", ko: "예상 비용" }),
      metric: summary?.averageEstimatedCostUsd,
    },
  ];

  return (
    <section style={{ display: "grid", gap: "16px" }}>
      <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        {resultItems.map((item) => (
          <article key={item.label} style={cardStyle()}>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase" }}>
              {item.label}
            </p>
            <p style={{ margin: "8px 0 0", fontSize: "1.3rem", fontWeight: 700 }}>{item.value}</p>
          </article>
        ))}
      </div>
      <div style={{ display: "grid", gap: "10px" }}>
        <h3 style={{ margin: 0, fontSize: "1rem" }}>
          {pickAdminDashboardLocaleText(locale, { en: "Average per run", ko: "건당 평균" })}
        </h3>
        <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
          {averageItems.map((item) => (
            <article key={item.label} style={cardStyle()}>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase" }}>
                {item.label}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "1.3rem", fontWeight: 700 }}>{item.metric?.label ?? "-"}</p>
              {item.metric?.contextLabel ? (
                <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: "0.78rem" }}>{item.metric.contextLabel}</p>
              ) : null}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RecognitionAuditScreenContent(props: {
  applyQuery: () => Promise<void>;
  explicitUpperBound?: string;
  locale: "ko" | "en";
  onResetDraft: () => void;
  onResetToLive: () => void;
  openDetail: (recognitionRunId: string) => void;
  state: RecognitionAuditScreenModelState;
  updateQueryDraft: (patch: Partial<AdminRecognitionAuditQuery>) => void;
}): JSX.Element {
  const copy = {
    section: pickAdminDashboardLocaleText(props.locale, { en: "Recognition Audits", ko: "인식 감사" }),
    title: pickAdminDashboardLocaleText(props.locale, { en: "Recognition Audit Board", ko: "인식 감사 보드" }),
    defaultScopeTitle: pickAdminDashboardLocaleText(props.locale, {
      en: "Default scope: start from the latest 100 recognition runs",
      ko: "기본 범위: 최신 인식 실행 100건부터 확인합니다",
    }),
    defaultScopeBody: pickAdminDashboardLocaleText(props.locale, {
      en: "Start with the latest 100 runs, then narrow by failure stage, model, account, or pet only when needed.",
      ko: "최신 100건부터 보고, 필요할 때만 실패 위치·모델·계정·반려동물로 좁힙니다.",
    }),
    loading: pickAdminDashboardLocaleText(props.locale, { en: "Loading recognition audit data...", ko: "인식 감사 데이터를 불러오는 중입니다..." }),
    emptyTitle: pickAdminDashboardLocaleText(props.locale, { en: "No recognition runs found", ko: "조회된 인식 실행이 없습니다" }),
    emptyBody: pickAdminDashboardLocaleText(props.locale, { en: "No recognition audit runs were returned for the current scope.", ko: "현재 범위에서 조회된 인식 실행이 없습니다." }),
    errorTitle: pickAdminDashboardLocaleText(props.locale, { en: "Recognition audit query failed", ko: "인식 감사 조회 실패" }),
    retry: pickAdminDashboardLocaleText(props.locale, { en: "Retry", ko: "재시도" }),
    sourceStatus: pickAdminDashboardLocaleText(props.locale, { en: "Source status", ko: "원본 상태" }),
    latestGeneratedAt: pickAdminDashboardLocaleText(props.locale, { en: "Generated", ko: "생성 시각" }),
    rawAccessHistory: pickAdminDashboardLocaleText(props.locale, { en: "Raw access history", ko: "Raw 접근 이력" }),
  };

  const readyOrEmpty = props.state.loadState.status === "ready" || props.state.loadState.status === "empty";

  return (
    <section style={{ display: "grid", gap: "16px" }}>
      <header style={{ display: "grid", gap: "8px" }}>
        <p style={{ margin: 0, color: "#9a3412", fontSize: "0.8rem", fontWeight: 700, textTransform: "uppercase" }}>
          {copy.section}
        </p>
        <h2 style={{ margin: 0, fontSize: "1.5rem" }}>{copy.title}</h2>
        <div style={cardStyle()}>
          <strong>{copy.defaultScopeTitle}</strong>
          <p style={{ margin: "8px 0 0", color: "#475569", lineHeight: 1.6 }}>{copy.defaultScopeBody}</p>
        </div>
      </header>

      {props.explicitUpperBound ? (
        <AdminLiveRangeBanner
          explicitUpperBound={props.explicitUpperBound}
          locale={props.locale}
          onResetToLive={props.onResetToLive}
        />
      ) : null}

      {renderSummaryCards(props.locale, props.state)}

      <AdminAggregateSummaryStrip
        ariaLabel={copy.sourceStatus}
        title={copy.sourceStatus}
        items={[
          { id: "generated", label: copy.latestGeneratedAt, value: props.state.page?.generatedAtLabel ?? "-" },
          { id: "rawHistory", label: copy.rawAccessHistory, value: String(props.state.detailData?.rawAccessHistory.length ?? 0) },
        ]}
      />

      <AdminSourceStatusGrid
        items={buildSourceStatusItems(props.locale, props.state)}
        statusLabel={copy.sourceStatus}
      />

      <RecognitionAuditFilterBar
        locale={props.locale}
        onApply={props.applyQuery}
        onReset={props.onResetDraft}
        queryDraft={props.state.queryDraft}
        updateQueryDraft={props.updateQueryDraft}
      />

      {props.state.loadState.status === "idle" || props.state.loadState.status === "loading" ? (
        <AdminEmptyState title={copy.loading} body={copy.defaultScopeBody} />
      ) : null}

      {props.state.loadState.status === "permission_denied" ? (
        <AdminErrorState title={props.state.loadState.error.message} tone="permission_denied" />
      ) : null}

      {props.state.loadState.status === "error" ? (
        <AdminErrorState
          title={copy.errorTitle}
          body={props.state.loadState.error.message}
          action={
            <button
              onClick={() => void props.applyQuery()}
              style={{ minHeight: "40px", padding: "0 14px", borderRadius: "12px", border: "1px solid rgba(30, 64, 175, 0.22)", background: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }}
              type="button"
            >
              {copy.retry}
            </button>
          }
        />
      ) : null}

      {readyOrEmpty ? (
        <AdminDataTable
          columns={[
            {
              id: "startedAt",
              label: props.state.page?.columns[0]?.label ?? "Started",
              defaultSortDirection: "desc",
              renderCell: (row) => row.startedAtLabel,
              sortValue: (row) => row.startedAtLabel,
            },
            {
              id: "document",
              label: props.state.page?.columns[1]?.label ?? "Document",
              renderCell: (row) => row.documentLabel,
            },
            {
              id: "status",
              label: props.state.page?.columns[2]?.label ?? "Status",
              renderCell: (row) => <AdminStatusBadge value={row.status.value} label={row.status.label} />,
            },
            {
              id: "outcome",
              label: props.state.page?.columns[3]?.label ?? "Outcome",
              renderCell: (row) => row.outcome.label,
            },
            {
              id: "failedStage",
              label: props.state.page?.columns[4]?.label ?? "Failed stage",
              renderCell: (row) => row.failedStage.label,
            },
            {
              id: "accountPet",
              label: props.state.page?.columns[5]?.label ?? "Account / Pet",
              renderCell: (row) => row.accountPetLabel,
            },
            {
              id: "source",
              label: props.state.page?.columns[6]?.label ?? "Source",
              renderCell: (row) => (
                <div style={{ display: "grid", gap: "6px" }}>
                  <span>{row.source.summaryLabel}</span>
                  <span style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {row.source.mimeTypeLabels.map((label) => (
                      <span
                        key={`${row.recognitionRunId}_${label}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          minHeight: "24px",
                          padding: "0 8px",
                          borderRadius: "999px",
                          border: "1px solid rgba(148, 163, 184, 0.28)",
                          background: "rgba(248, 250, 252, 0.9)",
                          color: "#475569",
                          fontSize: "0.76rem",
                          fontWeight: 600,
                        }}
                      >
                        {label}
                      </span>
                    ))}
                    {row.source.remainingMimeTypeCountLabel ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          minHeight: "24px",
                          padding: "0 8px",
                          borderRadius: "999px",
                          border: "1px solid rgba(148, 163, 184, 0.28)",
                          background: "rgba(248, 250, 252, 0.9)",
                          color: "#475569",
                          fontSize: "0.76rem",
                          fontWeight: 600,
                        }}
                      >
                        {row.source.remainingMimeTypeCountLabel}
                      </span>
                    ) : null}
                  </span>
                </div>
              ),
            },
            {
              id: "modelUsage",
              label: props.state.page?.columns[7]?.label ?? "Tokens / Cost",
              renderCell: (row) => row.modelUsageLabel,
            },
            {
              id: "reviewFinal",
              label: props.state.page?.columns[8]?.label ?? "Review / Final save",
              renderCell: (row) => row.reviewFinalLabel,
            },
            {
              id: "rawAccess",
              label: props.state.page?.columns[9]?.label ?? "Raw access",
              renderCell: (row) => row.rawAccess.label,
              align: "right",
            },
          ]}
          emptyState={<AdminEmptyState title={copy.emptyTitle} body={copy.emptyBody} />}
          getRowId={(row) => row.recognitionRunId}
          initialSortState={{ columnId: "startedAt", direction: "desc" }}
          onRowClick={(row) => props.openDetail(row.recognitionRunId)}
          rows={props.state.page?.rows ?? []}
        />
      ) : null}

    </section>
  );
}

export function RecognitionAuditScreen(props: RecognitionAuditScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const refreshTick = useAdminAutoRefreshTick(props.dependencies.runtimeConfig);
  const listSearchParams = useMemo(
    () => resolveRecognitionAuditListSearchParams(searchParams),
    [searchParams],
  );
  const defaultTimeRangeRefreshSeed = resolveRecognitionAuditRefreshSeed(listSearchParams, refreshTick);
  const explicitUpperBound = resolveExplicitAdminTimeRangeUpperBound(listSearchParams);
  const defaultTimeRange = useMemo(
    () => createDefaultAdminListTimeRange(),
    [defaultTimeRangeRefreshSeed],
  );
  const query = useMemo(
    () => parseRecognitionAuditQueryParams(listSearchParams, defaultTimeRange),
    [defaultTimeRange, listSearchParams],
  );
  const state = useRecognitionAuditScreenModel(
    useMemo(() => ({ ...props.dependencies, locale }), [props.dependencies, locale]),
    query,
  );
  const manualRefreshRegistration = useMemo(
    () => ({
      onRefresh: () => void state.load(),
      refreshing: state.loadState.status === "loading",
    }),
    [state.load, state.loadState.status],
  );
  useAdminManualRefreshRegistration(manualRefreshRegistration);

  const applyQuery = async () => {
    setSearchParams(toSearchParams({ ...state.queryDraft, cursor: undefined }, locale));
  };

  const resetDraft = () => {
    const resetQuery = createDefaultRecognitionAuditQuery();
    state.updateQueryDraft(resetQuery);
    setSearchParams(toSearchParams(resetQuery, locale));
  };

  const resetToLive = () => {
    const nextParams = clearExplicitAdminTimeRangeSearchParams(listSearchParams);
    setSearchParams(withAdminDashboardLocaleSearchParams(nextParams, locale));
  };

  const openDetail = createRecognitionAuditRowClickHandler(navigate, listSearchParams);

  return (
    <RecognitionAuditScreenContent
      applyQuery={applyQuery}
      explicitUpperBound={explicitUpperBound}
      locale={locale}
      onResetDraft={resetDraft}
      onResetToLive={resetToLive}
      openDetail={openDetail}
      state={state}
      updateQueryDraft={state.updateQueryDraft}
    />
  );
}
